import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { adminGetMyAccess } from "@/lib/admin-authz.functions";
import { ADMIN_SECTIONS } from "@/lib/admin-authz";
import { adminLandingNav } from "@/lib/admin-invite";

export const Route = createFileRoute("/accept-invite")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Accept your TruWe team invite" },
      {
        name: "description",
        content: "Set a password and get into your TruWe admin workspace.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AcceptInvitePage,
});

type Stage = "checking" | "expired" | "form" | "done";

function AcceptInvitePage() {
  const navigate = useNavigate();
  const router = useRouter();
  const [stage, setStage] = useState<Stage>("checking");
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchAccess = useServerFn(adminGetMyAccess);

  // The invite link carries the session in the URL hash; supabase-js consumes
  // it on load. Listen as well, because detection can finish after mount.
  useEffect(() => {
    let settled = false;
    const finish = (userEmail: string | null | undefined) => {
      if (settled) return;
      settled = true;
      setEmail(userEmail ?? null);
      setStage("form");
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      if (session?.user) finish(session.user.email);
    });

    supabase.auth.getSession().then(({ data }) => {
      if (data.session?.user) finish(data.session.user.email);
      else
        window.setTimeout(() => {
          if (!settled) {
            settled = true;
            setStage("expired");
          }
        }, 2500);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  async function resolveTarget(): Promise<{ to: string; search?: { section: string } }> {
    // Honour the section carried by the invite when the user really holds it,
    // otherwise fall back to their best available area.
    let sections: string[] = [];
    try {
      const access = await fetchAccess();
      sections = access?.sections ?? [];
    } catch {
      /* not staff yet — fall through to /account */
    }
    const wanted = new URLSearchParams(window.location.search).get("section");
    if (
      wanted &&
      (ADMIN_SECTIONS as readonly string[]).includes(wanted) &&
      sections.includes(wanted)
    ) {
      return { to: "/admin", search: { section: wanted } };
    }
    return adminLandingNav(sections);
  }

  async function goToWorkspace() {
    router.invalidate();
    const target = await resolveTarget();
    setStage("done");
    navigate({ to: target.to, search: target.search, replace: true } as never);
  }

  async function handleSetPassword(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success("Password set, welcome to the team");
      await goToWorkspace();
    } catch (err: any) {
      toast.error(err?.message || "Could not set your password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-12">
      <h1 className="font-display text-3xl font-semibold text-brand-deep">
        Accept your invite
      </h1>

      {stage === "checking" && (
        <p className="mt-3 text-sm text-muted-foreground">
          Verifying your invite link…
        </p>
      )}

      {stage === "expired" && (
        <>
          <p className="mt-3 text-sm text-muted-foreground">
            This invite link has expired or has already been used. You can still
            sign in with a WhatsApp one-time code using the mobile number your
            admin added.
          </p>
          <Button asChild className="mt-6 h-11 w-full">
            <Link to="/auth">Sign in with WhatsApp OTP</Link>
          </Button>
        </>
      )}

      {stage === "form" && (
        <>
          <p className="mt-2 text-sm text-muted-foreground">
            {email ? (
              <>
                Signed in as <span className="font-medium">{email}</span>. Set a
                password so you can get back in any time.
              </>
            ) : (
              "Set a password so you can get back in any time."
            )}
          </p>
          <form onSubmit={handleSetPassword} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="new-password">Create a password</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                At least 8 characters.
              </p>
            </div>
            <Button type="submit" className="h-11 w-full" disabled={busy}>
              {busy ? "Saving…" : "Set password & continue"}
            </Button>
          </form>
          <button
            type="button"
            className="mt-4 text-center text-sm text-muted-foreground hover:text-brand"
            onClick={() => void goToWorkspace()}
          >
            Skip. I&apos;ll sign in with WhatsApp OTP
          </button>
        </>
      )}

      {stage === "done" && (
        <p className="mt-3 text-sm text-muted-foreground">
          Taking you to your workspace…
        </p>
      )}
    </main>
  );
}
